// @ts-nocheck
/**
 * Description: Enriches Klevu search "Category" and "Pages" suggestions with
 *    Shopify collection/page data (image, title, description) so they can be
 *    rendered as image cards, similar to product cards.
 *
 *    Klevu re-renders the quick (dropdown) template on every keystroke, so the
 *    GraphQL results are cached in two tiers to avoid repeat calls:
 *      1. In-memory (`window.klevuContentInfo`) — reused across every Klevu
 *         re-render within the same page view.
 *      2. sessionStorage (with a TTL) — persists across full page navigations
 *         within the browser tab. Collections/pages change rarely, so each
 *         handle is fetched at most once per session.
 *
 *    Before every fetch, handles already present in the cache are filtered out,
 *    so only missing handles hit GraphQL, batched into a single aliased query.
 *    Handles that resolve to nothing are cached as `null` (negative caching) so
 *    they are not retried.
 */
import {
  fetchGraphQL,
  buildCollectionsInfoQuery,
  buildPagesInfoQuery,
  buildArticlesInfoQuery,
} from '@theme/tw-graphql';

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const SESSION_KEY = 'twKlevuContentInfo';
const LOADED_EVENT = 'klevuContentInfoLoaded';

const emptyStore = () => ({ collection: {}, page: {} });

/** Reads the sessionStorage cache, dropping expired entries. */
const loadSessionStore = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const store = emptyStore();
    for (const type of ['collection', 'page']) {
      const bucket = parsed?.[type] || {};
      for (const handle of Object.keys(bucket)) {
        const entry = bucket[handle];
        if (entry && now - entry.cachedAt <= CACHE_TTL_MS) {
          store[type][handle] = entry;
        }
      }
    }
    return store;
  } catch {
    return emptyStore();
  }
};

const saveSessionStore = (store) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(store));
  } catch {
    // sessionStorage unavailable/full; the in-memory cache still applies.
  }
};

// Two-tier cache: seed the in-memory store from sessionStorage once per page view.
window.klevuContentInfo = window.klevuContentInfo || loadSessionStore();

/** Returns cached data for a handle: the record, `null` if negatively cached, or `undefined` if unknown. */
export const getCachedContentInfo = (type, handle) => {
  const entry = window.klevuContentInfo?.[type]?.[handle];
  return entry ? entry.data : undefined;
};

/** Derives a Shopify handle from a Klevu result URL (last non-empty path segment). */
export const handleFromUrl = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.split('/').filter(Boolean).pop() || '';
  } catch {
    return '';
  }
};

const collectHandles = (results) =>
  (Array.isArray(results) ? results : [])
    .map((item) => handleFromUrl(item?.url))
    .filter(Boolean);

/**
 * Resolves a CMS URL to either a Shopify page handle or a blog/article pair.
 * Output key is used as the cache key in the `page` bucket.
 */
const cmsLocatorFromUrl = (url) => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const segments = path.split('/').filter(Boolean);
    const pagesIdx = segments.lastIndexOf('pages');
    if (pagesIdx >= 0 && segments[pagesIdx + 1]) {
      const pageHandle = segments[pagesIdx + 1];
      return {
        key: `page:${pageHandle}`,
        resource: 'page',
        pageHandle,
      };
    }
    const blogsIdx = segments.lastIndexOf('blogs');
    if (blogsIdx >= 0 && segments[blogsIdx + 1] && segments[blogsIdx + 2]) {
      const blogHandle = segments[blogsIdx + 1];
      const articleHandle = segments[blogsIdx + 2];
      return {
        key: `article:${blogHandle}/${articleHandle}`,
        resource: 'article',
        blogHandle,
        articleHandle,
      };
    }
    const fallbackHandle = segments[segments.length - 1] || '';
    if (!fallbackHandle) return null;
    return {
      key: `page:${fallbackHandle}`,
      resource: 'page',
      pageHandle: fallbackHandle,
    };
  } catch {
    return null;
  }
};

const collectCmsLocators = (results) =>
  (Array.isArray(results) ? results : [])
    .map((item) => cmsLocatorFromUrl(item?.url))
    .filter(Boolean);

const missingHandles = (type, handles) =>
  [...new Set(handles)].filter(
    (handle) =>
      window.klevuContentInfo[type][handle] === undefined
  );

const storeResults = (type, entries) => {
  const now = Date.now();
  for (const { handle, data } of entries) {
    window.klevuContentInfo[type][handle] = {
      data: data || null,
      cachedAt: now,
    };
  }
  saveSessionStore(window.klevuContentInfo);
};

const fetchByType = async (type, handles, buildQuery, alias) => {
  const missing = missingHandles(type, handles);
  if (!missing.length) return;
  const result = await fetchGraphQL(buildQuery(missing));
  const data = result?.data;
  if (!data) return;
  storeResults(
    type,
    missing.map((handle, index) => ({
      handle,
      data: data[`${alias}${index}`],
    })),
  );
};

const fetchCmsByLocator = async (locators) => {
  const unique = [
    ...new Map(locators.map((locator) => [locator.key, locator])).values(),
  ];
  const missingLocators = unique.filter(
    (locator) =>
      window.klevuContentInfo.page[locator.key] === undefined ||
      window.klevuContentInfo.page[locator.key].data === null,
  );
  if (!missingLocators.length) return;

  const pageLocators = missingLocators.filter(
    (locator) => locator.resource === 'page',
  );
  const articleLocators = missingLocators.filter(
    (locator) => locator.resource === 'article',
  );

  if (pageLocators.length) {
    const result = await fetchGraphQL(
      buildPagesInfoQuery(pageLocators.map((locator) => locator.pageHandle)),
    );
    const data = result?.data;
    if (data) {
      storeResults(
        'page',
        pageLocators.map((locator, index) => ({
          handle: locator.key,
          data: data[`p${index}`],
        })),
      );
    }
  }

  if (articleLocators.length) {
    const result = await fetchGraphQL(
      buildArticlesInfoQuery(
        articleLocators.map((locator) => ({
          blogHandle: locator.blogHandle,
          articleHandle: locator.articleHandle,
        })),
      ),
    );
    const data = result?.data;
    if (data) {
      storeResults(
        'page',
        articleLocators.map((locator, index) => ({
          handle: locator.key,
          data: data[`a${index}`]?.articleByHandle,
        })),
      );
    }
  }
};

/** Klevu passes records under `response.data.queryResults`; the template shape uses `query.<id>.result`. */
const recordsForQuery = (data, queryId) => {
  const fromResponse = data?.response?.data?.queryResults?.find(
    (query) => query?.id === queryId,
  )?.records;
  if (Array.isArray(fromResponse)) return fromResponse;
  const fromTemplate = data?.query?.[queryId]?.result;
  return Array.isArray(fromTemplate) ? fromTemplate : [];
};

/**
 * Extracts category/page handles from a Klevu render payload, fetches any that
 * are not already cached, and dispatches `klevuContentInfoLoaded` so cards can
 * hydrate.
 * @param {object} data - The Klevu template render data.
 */
export const fetchAndCacheContentInfo = async (data) => {
  const collectionHandles = collectHandles(
    recordsForQuery(data, 'categoryCompressed'),
  );
  const cmsLocators = collectCmsLocators(
    recordsForQuery(data, 'cmsCompressed'),
  );
  if (!collectionHandles.length && !cmsLocators.length) return;

  await Promise.all([
    fetchByType(
      'collection',
      collectionHandles,
      buildCollectionsInfoQuery,
      'c',
    ),
    fetchCmsByLocator(cmsLocators),
  ]);

  document.dispatchEvent(
    new CustomEvent(LOADED_EVENT, { detail: window.klevuContentInfo }),
  );
};

const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char],
  );

const imageFromInfo = (type, info) =>
  type === 'page'
    ? info?.featuredImage?.reference?.image?.url || info?.image?.url
    : info?.image?.url;

/**
 * `<tw-klevu-content-card data-type="collection|page" data-url="..." data-name="...">`
 * Renders a link/name immediately, then upgrades to an image card once the
 * matching Shopify data is cached (either already present, or via the
 * `klevuContentInfoLoaded` event).
 */
class TWKlevuContentCard extends HTMLElement {
  connectedCallback() {
    this.type = this.dataset.type === 'page' ? 'page' : 'collection';
    this.url = this.dataset.url || '#';
    this.name = this.dataset.name || '';
    this.handle =
      this.type === 'page'
        ? cmsLocatorFromUrl(this.url)?.key || handleFromUrl(this.url)
        : handleFromUrl(this.url);

    this.renderCard(getCachedContentInfo(this.type, this.handle));

    // undefined means "not fetched yet"; null means "fetched, no match".
    if (getCachedContentInfo(this.type, this.handle) === undefined) {
      this.onLoaded = () => {
        const info = getCachedContentInfo(this.type, this.handle);
        if (info === undefined) return;
        this.renderCard(info);
        document.removeEventListener(LOADED_EVENT, this.onLoaded);
        this.onLoaded = null;
      };
      document.addEventListener(LOADED_EVENT, this.onLoaded);
    }
  }

  disconnectedCallback() {
    if (this.onLoaded)
      document.removeEventListener(LOADED_EVENT, this.onLoaded);
  }

  renderCard(info) {
    const title = info?.title || this.name;
    const description = info?.description || info?.bodySummary || '';
    const image = imageFromInfo(this.type, info);
    const displayImage = image || window.twKlevuFallbackImage || '';
    // Real images fill the frame; the logo fallback is contained so it isn't cropped.
    const imageClass = image
      ? 'rounded-lg w-full object-cover mb-6'
      : 'rounded-lg w-full object-contain bg-background-page p-6 mb-6';

    this.innerHTML = `
      <a
        href="${escapeHtml(this.url)}"
        class="thermoworks-collection-card block bg-background-neutral-faded hover:bg-background-neutral-faded-hover rounded-lg p-6 group transition-colors ease-out duration-300 h-full"
        data-type="${escapeHtml(this.type)}"
      >
        ${
          displayImage
            ? `<img src="${escapeHtml(displayImage)}" alt="${escapeHtml(title)}" width="254" height="254" loading="lazy" class="${imageClass}" />`
            : ''
        }
        <span class="flex justify-between items-start gap-4">
          <span class="flex flex-col gap-1">
            <span class="text-body-two leading-body-two font-medium text-background-neutral">${escapeHtml(title)}</span>
            ${
              description
                ? `<span class="text-body-three leading-body-three font-normal text-foreground-neutral-faded line-clamp-2">${escapeHtml(description)}</span>`
                : ''
            }
          </span>
          <span class="bg-foreground-primary border border-foreground-primary rounded-md transition-colors ease-out duration-300 md:bg-transparent md:border-black/10 md:group-hover:bg-foreground-primary md:group-hover:border-foreground-primary shrink-0">
            <span class="block self-start p-2">
              <span class="block p-[2px]">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"></path></svg>
              </span>
            </span>
          </span>
        </span>
      </a>
    `;
  }
}

if (!customElements.get('tw-klevu-content-card')) {
  customElements.define('tw-klevu-content-card', TWKlevuContentCard);
}

// Register on both Klevu surfaces: quick (dropdown, all pages) and landing (search page).
const registerKlevuHooks = () => {
  if (!window.klevu?.afterTemplateRender) return false;
  window.klevu.afterTemplateRender('quick', (data) => {
    fetchAndCacheContentInfo(data);
  });
  window.klevu.afterTemplateRender('landing', (data) => {
    fetchAndCacheContentInfo(data);
  });
  return true;
};

if (!registerKlevuHooks()) {
  // Klevu core may not be ready yet; retry briefly until it is.
  let attempts = 0;
  const timer = setInterval(() => {
    if (registerKlevuHooks() || ++attempts > 40) clearInterval(timer);
  }, 100);
}
